import {
  Button,
  CondensedHeader,
  CondensedMedium,
  Form,
  FormGroup,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  LargeHeader,
  Layout,
  Paragraph,
  Radio,
  Stretched,
  Well,
} from '@axiom/ui';
import React, { useEffect } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router';
import { userIntentValues } from '@axiom/const';

import { useUserAccountData } from '../../hooks/useUserAccountData';
import { localStorageUtil } from '../../utils/local-storage-util';

import { ClientIntentSchema } from './client-intent-schema';
import type { ClientIntentForm } from './client-intent-schema';

export type UserIntentType =
  (typeof userIntentValues)[keyof typeof userIntentValues];

const intentToWeeksMap: Partial<Record<UserIntentType, number>> = {
  [userIntentValues.Asap]: 1,
  [userIntentValues.FewWeeks]: 3,
};

export const ClientIntent = () => {
  const { account, user } = useUserAccountData();
  const navigate = useNavigate();
  const initialValues = { userIntent: userIntentValues.Asap };

  useEffect(() => {
    localStorageUtil.intakeIntroShown(user.id).set(true);
  }, []);

  const calculateStartDate = (intent: UserIntentType): string => {
    const weeksToAdd = intentToWeeksMap[intent] || 0;
    return moment().add(weeksToAdd, 'w').format('YYYY-MM-DD');
  };

  const isHighIntent = (intent: UserIntentType): boolean => {
    return (
      intent === userIntentValues.Asap || intent === userIntentValues.FewWeeks
    );
  };

  const handleSubmit = (formData: ClientIntentForm) => {
    const { userIntent } = formData;

    if (isHighIntent(userIntent)) {
      const startDate = calculateStartDate(userIntent);
      void navigate(`/account/${account.id}/intake`, { state: { startDate } });
    } else {
      void navigate('/browse-talent');
    }
  };

  return (
    <Grid name="CLIENT_INTENT">
      <GridRow gutterBottom="24px" gutterTop="30px" centered>
        <GridColumn largeScreenWidth={10} smallScreenWidth={12}>
          <Well background="high attention">
            <LargeHeader name="INTENT_HEADER">
              Hi {user.firstName}. How soon do you need to engage legal talent?
            </LargeHeader>
            <Gutter bottom="8px" />
            <Paragraph>
              Your timeframe determines the best way to connect you with Axiom
              talent.
            </Paragraph>
          </Well>
        </GridColumn>
      </GridRow>
      <GridRow centered>
        <GridColumn
          textAlign="center"
          largeScreenWidth={10}
          smallScreenWidth={12}
        >
          <Form
            name="CLIENT_INTENT_FORM"
            schema={ClientIntentSchema}
            initialValues={initialValues}
            onSubmit={formData => {
              handleSubmit({
                userIntent: formData.userIntent ?? initialValues.userIntent,
              });
            }}
          >
            {({ fireSubmit }) => (
              <>
                <Gutter bottom="24px">
                  <FormGroup>
                    <Stretched>
                      <Grid>
                        <GridColumn
                          largeScreenWidth={4}
                          tabletWidth={4}
                          mobileWidth={12}
                          stretched
                        >
                          <Radio
                            mode="tile"
                            name="userIntent"
                            displayValue={
                              <Layout>
                                <CondensedHeader>
                                  As soon as possible
                                </CondensedHeader>
                              </Layout>
                            }
                            option={userIntentValues.Asap}
                          />
                          <Gutter bottom="16px" only="mobile" />
                        </GridColumn>
                        <GridColumn
                          largeScreenWidth={4}
                          tabletWidth={4}
                          mobileWidth={12}
                          stretched
                        >
                          <Radio
                            mode="tile"
                            name="userIntent"
                            displayValue={
                              <Layout>
                                <CondensedHeader>
                                  Within the next few weeks
                                </CondensedHeader>
                              </Layout>
                            }
                            option={userIntentValues.FewWeeks}
                          />
                          <Gutter bottom="16px" only="mobile" />
                        </GridColumn>
                        <GridColumn
                          largeScreenWidth={4}
                          tabletWidth={4}
                          mobileWidth={12}
                          stretched
                        >
                          <Radio
                            mode="tile"
                            name="userIntent"
                            displayValue={
                              <Layout>
                                <CondensedHeader>
                                  I'm just looking
                                </CondensedHeader>
                              </Layout>
                            }
                            option={userIntentValues.JustLooking}
                          />
                        </GridColumn>
                      </Grid>
                    </Stretched>
                  </FormGroup>
                </Gutter>
                <div>
                  <Gutter bottom="16px">
                    <CondensedMedium>
                      Find Axiom legal pros who match your legal team's needs.
                    </CondensedMedium>
                  </Gutter>
                  <Button
                    name="SUBMIT"
                    onClick={async () => {
                      await fireSubmit();
                    }}
                  >
                    Continue
                  </Button>
                </div>
              </>
            )}
          </Form>
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
